import { Highlight } from '@sportninja/common/hooks/useHighlights'

export interface Player {
  id: string
  name: string
  // Add other player properties as needed
}

export interface Team {
  id: string
  name: string
  // Add other team properties as needed
}

/**
 * Maps highlights with player and team names from roster data
 * @param highlights - Array of highlight objects
 * @param rosterPlayersById - Map of player IDs to player objects
 * @param teamsById - Map of team IDs to team objects
 * @returns Array of highlights with added playerName and teamName properties
 */
export const mapHighlightsWithNames = (
  highlights: Highlight[],
  rosterPlayersById: Record<string, Player>,
  teamsById: Record<string, Team>
): Highlight[] => {
  return highlights.map((highlight) => ({
    ...highlight,
    playerName: rosterPlayersById[highlight.player_id]?.name ?? '',
    teamName: teamsById[highlight.team_id]?.name ?? '',
  }))
}

/**
 * Determines team side (Home/Visitor) based on team ID and game teams
 * @param teamId - The team ID from the highlight
 * @param homeTeamId - The home team ID from the game
 * @param visitingTeamId - The visiting team ID from the game
 * @returns 'Home' | 'Visitor' | undefined
 */
export const getTeamSide = (
  teamId: string,
  homeTeamId?: string,
  visitingTeamId?: string
): 'Home' | 'Visitor' | undefined => {
  if (teamId === homeTeamId) return 'Home'
  if (teamId === visitingTeamId) return 'Visitor'
  return undefined
}

/**
 * Enriches highlights with team side information
 * @param highlights - Array of highlight objects
 * @param homeTeamId - The home team ID from the game
 * @param visitingTeamId - The visiting team ID from the game
 * @returns Array of highlights with added teamSide property
 */
export const enrichHighlightsWithTeamSide = (
  highlights: Highlight[],
  homeTeamId?: string,
  visitingTeamId?: string
): Highlight[] => {
  return highlights.map((highlight) => ({
    ...highlight,
    teamSide: getTeamSide(highlight.team_id, homeTeamId, visitingTeamId),
  }))
}
